import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import {
  CalendarApi,
  CalendarOptions,
  DateSelectArg,
  EventClickArg,
  EventDropArg,
  EventInput,
} from '@fullcalendar/core';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  debounceTime,
  filter,
  map,
  merge,
  Observable,
  of,
  pairwise,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs';
import moment from 'moment';

import { Schedule } from '@shared/schedule/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { Resource } from '@shared/resource/models';
import { Service } from '@shared/service/models';
import { AuthUser } from '@shared/signup/models';
import { PartyTypes } from '@shared/party/enums';
import { Party } from '@shared/party/models';
import {
  ConfigurePartyService,
  ConfirmationModalService,
  DateTimeService,
  ProviderScheduleService,
} from '@tablesready/common-ui';
import {
  PartiesService,
  ResourcesService,
  ScheduleService,
  ServicesService,
} from '../../../services';
import { DetailPartyComponent } from '../detail-party';
import {
  defaultDetailModalActions,
  getCalendarOptions,
  legends,
  sort,
  statusStyles,
} from './day-grid.settings';
import { BusinessHours, Legends } from './interfaces';
import { NewTimelinePartyData, PartyActionEvent } from '../../models';
import { FloorPlanPartyType, PartyActions } from '../../enums';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-day-grid-view',
  templateUrl: './day-grid-view.component.html',
  styleUrls: ['./day-grid-view.component.scss'],
})
// TODO: rework for optimization
export class DayGridViewComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() user: AuthUser;
  @Input() minDate;
  @Input() getResourceName: (resource: Resource) => string;
  @Input() reservationGridDateFilter: NgbDateStruct;
  @Input() isOnFreePlan = true;

  @Output() updatePartiesCountEvent = new EventEmitter<number>();
  @Output() createNewParty = new EventEmitter<NewTimelinePartyData>();
  @Output() partyEdit = new EventEmitter<PartyActionEvent>();
  @Output() partyDelete = new EventEmitter<PartyActionEvent>();
  @Output() partyChat = new EventEmitter<PartyActionEvent>();
  @Output() partyCheckIn = new EventEmitter<PartyActionEvent>();
  @Output() partyComplete = new EventEmitter<PartyActionEvent>();
  @Output() partyServed = new EventEmitter<PartyActionEvent>();
  @Output() chooseDate = new EventEmitter<NgbDateStruct>();

  @ViewChild('calendar') calendarComponent: FullCalendarComponent;

  icon = AppIcon;
  iconSize = AppIconSize;
  partyType = PartyTypes.reservation;
  debounceMs = 200; // eslint-disable-line @typescript-eslint/no-magic-numbers
  services: Service[];
  statusStyles = statusStyles;
  sort: string[] = sort;
  legends: Legends = legends;
  now: number;
  resources: Resource[];
  schedule: Schedule[];
  timezone: string;
  generalSchedule: Schedule;
  hoursWithDate: Array<[number, number]>;
  sortValue: string;
  reservationDateFilter: number;
  showDayGrid: boolean;
  parties: Party[];
  isCurrentDay: boolean;
  calendarApi: CalendarApi;
  calendarOptions: CalendarOptions;
  isLoading = false;
  longestResourceNameCharCount = 0;
  resourceNameCharWidth = 6;

  private readonly renderCalendar$ = new Subject<boolean>();
  private readonly partiesByWorkTimeData = new BehaviorSubject<any>(null);
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly clockService: ClockService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly scheduleService: ScheduleService,
    private readonly partiesService: PartiesService,
    private readonly alertsService: AlertsService,
    private readonly dateTimeService: DateTimeService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly providerScheduleService: ProviderScheduleService,
    private readonly renderer: Renderer2,
    private readonly configureParty: ConfigurePartyService,
    private readonly socketService: SocketService,
    private readonly authRestaurantService: AuthRestaurantService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) { }

  sortItemName(sort: string): string {
    return `Sort by ${sort}`;
  }

  ngAfterViewInit(): void {
    const RENDER_DELAY = 500;
    const renderSub = this.renderCalendar$
      .pipe(
        tap(() => {
          this.isLoading = true;
        }),
        debounceTime(RENDER_DELAY),
      )
      .subscribe(() => {
        this.isLoading = false;

        this.initCalendar();
        this.getPartiesByWorkHours();

        this.isCurrentDay = this.isToday();
        this.scrollIfToday();
      });
    this.subscriptions.add(renderSub);

    this.subscriptions.add(combineLatest([
      this.servicesService.selectAllEnabled(),
      this.resourcesService.selectAllEnabled(),
      this.scheduleService.selectAll().pipe(filter(schedules => schedules.length > 0)),
      this.authRestaurantService.select().pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => prev === null ||
          (prev.location?._id !== current.location?._id && !!current.location?._id) ||
          (!!current.location._id && prev.location.timezone !== current.location.timezone)),
        map(([, current]) => current),
      ),
    ]).subscribe(([services, resources, schedule, user]) => {
      this.services = services;
      this.resources = resources;
      this.schedule = schedule;
      this.generalSchedule = this.schedule.find(item => item.default);

      this.resources.forEach((resource) => {
        if (resource.name.length > this.longestResourceNameCharCount) {
          let resourceName = resource.name;

          if (this.getResourceName) {
            resourceName = this.getResourceName(resource);
          }

          this.longestResourceNameCharCount = resourceName.length;
        }
      });

      if (user.location.timezone !== this.timezone) {
        this.timezone = user.location.timezone;
        this.renderCalendar$.next(true);
      }

      this.setDefaultSortValue();
      this.reservationGridDateFilter ? this.update() : this.changeDateToToday();
    }));
  }

  initCalendar(): void {
    this.calendarOptions = {
      ...getCalendarOptions(this.user.location?.resourceSettings?.label || 'Resources', this.appVariables.FULL_CALENDAR_KEY),
      resourceAreaWidth: `${(this.resourceNameCharWidth * this.longestResourceNameCharCount) + 100}px`,
      timeZone: this.timezone,
      now: (): string => moment(this.now).tz(this.timezone)
        .toISOString(true),
      select: this.handleDateSelect.bind(this),
      eventClick: this.handleEventClick.bind(this),
      eventResize: this.handleEventResize.bind(this),
      eventDrop: this.handleEventDrop.bind(this),
      eventDidMount: this.handleEventDidMount.bind(this),
      selectConstraint: 'businessHours',
      eventContent: this.handleEventContent.bind(this),
    };
  }

  ngOnInit(): void {
    this.renderCalendar$.next(true);
    this.subscriptions.add(this.clockService.clock$.subscribe(now => this.now = now));

    const partiesStream = this.socketService.getEventStream(WebSocketEventsEnum.parties);
    const reservationsStream = this.socketService.getEventStream(WebSocketEventsEnum.reservations);
    const TextSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.TextSmsEvent);
    const VoiceSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.VoiceSmsEvent);
    const InboundSmsEventStream = this.socketService.getEventStream(WebSocketEventsEnum.InboundSmsEvent);
    this.subscriptions.add(
      merge(partiesStream, reservationsStream, TextSmsEventStream, VoiceSmsEventStream, InboundSmsEventStream)
        .subscribe(() => {
          this.getPartiesByWorkHours();
        }),
    );
  }

  changeDateForGrid(): void {
    if (!this.reservationGridDateFilter) {
      this.changeDateToToday();
    }

    const timestamp = this.dateTimeService
      .ngbDateToMoment(this.reservationGridDateFilter, this.timezone)
      .valueOf();

    if (!isNaN(timestamp)) {
      this.chooseDate.emit(this.reservationGridDateFilter);
      this.update();
    }
  }

  selectPreviousDay(): void {
    const momentDate = this.dateTimeService.ngbDateToMoment(this.reservationGridDateFilter, this.timezone);
    const previousDayMomentDate = momentDate.subtract(1, 'day');

    this.reservationGridDateFilter = this.dateTimeService.momentToNgbDate(previousDayMomentDate);
    this.changeDateForGrid();
  }

  selectNextDay(): void {
    const momentDate = this.dateTimeService.ngbDateToMoment(this.reservationGridDateFilter, this.timezone);
    const nextDayMomentDate = momentDate.add(1, 'day');

    this.reservationGridDateFilter = this.dateTimeService.momentToNgbDate(nextDayMomentDate);
    this.changeDateForGrid();
  }

  changeDateToToday(): void {
    const date = moment();
    this.reservationGridDateFilter = {
      year: date.year(),
      month: date.month() + 1,
      day: date.date(),
    };
    this.chooseDate.emit(this.reservationGridDateFilter);
    this.update();
  }

  update(): void {
    this.reservationDateFilter = this.dateTimeService
      .ngbDateToMoment(this.reservationGridDateFilter, this.timezone).valueOf();

    const { hours, exceptions } = this.generalSchedule;
    this.hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      this.reservationDateFilter,
      this.timezone,
    );
    this.getPartiesByWorkHours();
  }

  configureGetPartiesReq(): Observable<Party[]> {
    if (this.user.location.bookingOptions?.servingTracking) {
      return this.partiesService.getPartiesByWorkTime({
        workTime: this.hoursWithDate,
        served: null,
        type: null,
      });
    }

    return this.partiesService.getPartiesByWorkTime({
      workTime: this.hoursWithDate,
      type: this.partyType,
      served: null,
    });
  }

  getPartiesByWorkHours(): void {
    if (this.isLoading) {
      return;
    }

    this.showDayGrid = true;
    if (!this.hoursWithDate?.length) {
      this.showDayGrid = false;

      return;
    }

    this.subscriptions.add(
      this.partiesByWorkTimeData.asObservable()
        .pipe(
          combineLatestWith(this.authRestaurantService.selectWithAccount()),
          throttleTime(this.debounceMs),
          switchMap(() => this.configureGetPartiesReq()),
          tap((parties) => {
            this.changeSort(this.sortValue);
            this.parties = parties;
            this.updatePartiesCountEvent.emit(this.parties.length);
            this.calendarApi = this.calendarComponent.getApi();
            this.calendarApi.gotoDate(this.reservationDateFilter);

            this.drawResources();
            this.drawBusinessHours();
            this.drawParties();
          }),
          catchError(error => {
            this.showDayGrid = false;
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'parties:',
              body: error,
            });

            return of(null);
          }),
        ).subscribe());
  }

  changeSort(sortValue) {
    const calendarApi = this.calendarComponent.getApi();
    if (sortValue === 'category') {
      calendarApi.setOption('resourceGroupField', sortValue);
      calendarApi.setOption('resourceOrder', `${sortValue},name`);
    } else {
      calendarApi.setOption('resourceGroupField', 'belongTo');
      calendarApi.setOption('resourceOrder', sortValue);
    }
  }

  getPartyStyles(party) {
    if (party?.seated_time) {
      return this.statusStyles.served;
    }

    if (party.checkin_time) {
      return this.statusStyles.checkIn;
    }

    if (party?.quoted_time < this.now) {
      return this.statusStyles.notChecked;
    }

    return this.statusStyles.notServed;
  }

  drawBusinessHours() {
    const workTimeIntervals = this.providerScheduleService.getWorkingHoursForDayGrid(
      this.generalSchedule,
      this.reservationDateFilter,
      this.timezone,
    );

    this.calendarOptions.slotMinTime = workTimeIntervals.length ? `${workTimeIntervals[0][0]}:00` : '00:00:00'; // HH:mm:ss
    this.calendarOptions.businessHours = workTimeIntervals.map(hoursSlot => ({
      startTime: hoursSlot[0],
      endTime: hoursSlot[1],
      daysOfWeek: String(moment.tz(this.reservationDateFilter, this.timezone).day()), // Day of week,
    }));
    // We need to do it as last action, the reason is slotMinTime option
    setTimeout(this.scrollIfToday.bind(this), 0);
  }

  scrollIfToday(): void {
    if (!this.isCurrentDay || !this.calendarApi) {
      return;
    }

    this.calendarApi.updateSize();
    this.calendarApi.scrollToTime(moment.tz(this.now, this.timezone).subtract(1, 'hour')
      .format('HH:mm:ss'));
  }

  drawResources(): void {
    this.calendarApi = this.calendarComponent.getApi();
    for (const resource of this.calendarApi.getResources()) {
      resource.remove();
    }

    const availableServicesIds = this.services.map(service => service._id);

    this.resources.forEach((resource) => {
      const { _id, name, order, category, capacity, services } = resource;

      const isAvailableService = services.some(service => availableServicesIds.includes(service));
      if (!isAvailableService) {
        return;
      }

      const businessHours = this.getBusinessHours(services);
      if (businessHours.length === 0) {
        return;
      }

      this.calendarApi.addResource({
        id: _id,
        title: this.getResourceName(resource),
        order,
        name,
        category: category || 'Resources',
        belongTo: 'All Resources',
        businessHours,
      });
    });
  }

  getBusinessHours(services: string[]): BusinessHours[] {
    return services.reduce((businessHours: BusinessHours[], serviceId: string) => {
      const scheduleId = this.services.find(service => service._id === serviceId)?.schedule;
      if (!scheduleId) {
        return [...businessHours];
      }
      const schedule = this.schedule.find(schedule => schedule._id === scheduleId);

      const hours = this.providerScheduleService.getWorkingHoursForDayGrid(
        schedule,
        this.reservationDateFilter,
        this.timezone,
      );

      const daysOfWeek = [moment.tz(this.reservationDateFilter, this.timezone).day()];

      const workingHours = hours.map(([startTime, endTime]) => ({
        startTime,
        endTime,
        daysOfWeek,
      }));

      return [...businessHours, ...workingHours];
    }, []);
  }

  drawParties(): void {
    this.calendarApi.removeAllEvents();
    if (!this.parties.length) {
      return;
    }

    this.parties.map((party) => {
      const { startISOTime, endISOTime } = this.getStartEndTimeParty(party);
      const { _id, name, resources, size } = party;
      let eventTitle = name || '';

      const partyStylesObj = this.getPartyStyles(party);
      const event: EventInput = {
        ...partyStylesObj,
        id: _id,
        resourceIds: resources,
        title: eventTitle,
        start: startISOTime,
        end: endISOTime,
        note: party.note,
        constraint: 'businessHours',
        editable: true,
        resourceEditable: true,
      };

      if (size) {
        event.size = size;
      }

      if (party?.seated_time) {
        event.editable = false;
        event.resourceEditable = false;
        event.durationEditable = true;
      } else if (resources.length > 1) {
        event.editable = false;
        event.resourceEditable = false;
        event.durationEditable = true;
      }

      this.calendarApi.addEvent(event);
    });
  }

  getStartEndTimeParty(party: Party) {
    const startTime =
      party.type !== PartyTypes.reservation && party.seated_time ? party.seated_time : party.quoted_time;
    const endTime =
      startTime + party.duration || this.user.location.bookingOptions.bookingInterval;
    const startISOTime = this.dateTimeService.timestampToISOString(startTime, this.timezone);
    const endISOTime = this.dateTimeService.timestampToISOString(endTime, this.timezone);

    return {
      startISOTime,
      endISOTime,
    };
  }

  addNewParty(time: number, resourceId: string, endTime: number): void {
    if (time < this.now) {
      this.confirmationModalService.confirm({
        content: 'Are you sure you want to add a party in the past period of time?',
        confirmBtnText: 'Ok',
      }).subscribe((result) => {
        if (!result.isConfirmed) {
          return;
        }

        this.createNewParty.emit({
          time,
          resourceId,
          endTime,
        });
      });

      return;
    }

    this.createNewParty.emit({
      time,
      resourceId,
      endTime,
    });
  }

  editParty({ partyId, startStr, endStr }): void {
    const selectedParty = this.parties.find(party => party._id === partyId);
    const partyInfo = {
      ...selectedParty,
      time: startStr,
      endTime: endStr,
    };
    this.modalController.create<DetailPartyComponent, { eventType: PartyActions }>(DetailPartyComponent, {
      user: this.user.location,
      timezone: this.timezone,
      party: partyInfo,
      services: this.services,
      resources: this.resources,
      showActions: defaultDetailModalActions,
      isRestaurantOnFreePlan: this.isOnFreePlan,
    })
      .pipe(
        take(1),
        filter(response => response !== undefined),
        tap(({ eventType }) => {
          const event: PartyActionEvent = {
            party: selectedParty,
            cardType: FloorPlanPartyType.booked,
          };

          switch (eventType) {
            case PartyActions.addEditReservation:
              this.partyEdit.emit(event);
              break;
            case PartyActions.confirmDelete:
              this.partyDelete.emit(event);
              break;
            case PartyActions.chatParty:
              this.partyChat.emit(event);
              break;
            case PartyActions.checkIn:
              this.partyCheckIn.emit(event);
              break;
            case PartyActions.serveParty:
              this.partyServed.emit(event);
              break;
            case PartyActions.completeParty:
              this.partyComplete.emit(event);
              break;
            default:
              break;
          }
        }),
        catchError((error) => {
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'ERROR:',
            body: 'Party no longer available',
          });

          return of(error);
        })
      )
      .subscribe();
  }

  checkResourcesCapacity(resources: Resource[], selectedResources: string[], party: Party): boolean {
    const resourcesCapacity = this.configureParty.calculateCapacityInSelectedResources(resources, selectedResources);

    if (this.user?.location.serviceSettings?.enabled &&
      this.user?.location.bookingOptions?.onlineReservationsEnabled) {
      let sizeAlertConfig: { deltaResourcesSize: number; determiner: string } | null = null;

      if (party?.size < resourcesCapacity.min) {
        sizeAlertConfig = {
          deltaResourcesSize: resourcesCapacity.min - party.size,
          determiner: 'less',
        };
      }
      if (party?.size > resourcesCapacity.max) {
        sizeAlertConfig = {
          deltaResourcesSize: party.size - resourcesCapacity.max,
          determiner: 'more',
        };
      }

      if (sizeAlertConfig) {
        this.confirmationModalService.confirm({
          content: `Sorry, the ${this.user.location.displayOptions?.labels?.customer || 'customer'} size
          is ${sizeAlertConfig.deltaResourcesSize}
          ${sizeAlertConfig.determiner} than the ${this.user.location.resourceSettings?.label || 'resource'} capacity.
          Please, change size first.`,
          closeBtnText: 'Cancel',
          confirmBtnText: 'Ok',
        });

        return false;
      }
    }

    return true;
  }

  setDefaultSortValue(): void {
    this.sortValue = this.user.location.resourceSettings.sort;
  }

  private isToday(): boolean {
    return moment.tz(this.now, this.timezone).format('D-M-YYYY') ===
      moment.tz(this.reservationDateFilter, this.timezone).format('D-M-YYYY');
  }

  private getIntersection(a: string[], b: string[]): string[] {
    const set1 = new Set(a);
    const set2 = new Set(b);

    const intersection = [...set1].filter(
      element => set2.has(element)
    );

    return intersection;
  }

  // Calendar methods
  handleEventContent(eventInfo) {
    const { event } = eventInfo;
    const range = event._instance.range as { start: Date; end: Date };

    const startDate = moment(range.start);
    const endDate = moment(range.end);

    const duration = moment.duration(endDate.diff(startDate));
    const differenceInMinutes = Number(duration.asMinutes());
    const lengthDivider = 3.3;

    let title = event.title;
    let maxLength = Math.trunc(Math.abs(differenceInMinutes / lengthDivider));

    if (title.length > maxLength) {
      title = `${title.substring(0, maxLength - 3)}...`;
    }

    const note = event.extendedProps.note;
    let notesString = '';
    if (this.user.location.bookingOptions.partyNotesOnTimeline && note) {
      notesString = Array.isArray(note) ? note.join(', ') : note;
      notesString = ` - ${notesString}`;
    }

    const insertHtmlSize = `<div>
      <div class="text-truncate">${title}${notesString}</div>
      <div class="text-truncate">${event.extendedProps?.size || ''}
        <span class="bi bi-people-fill"></span>
      </div>
    </div>`;

    return { html: insertHtmlSize };
  }

  handleEventDidMount(info) {
    const { el, event } = info;

    if (event?._def?.resourceIds?.length > 1) {
      const resourcesNames = event
        .getResources()
        .filter(elem => !!elem)
        .map(elem => elem._resource.title)
        .join(',');
      const tooltipText = `Multi table party (${resourcesNames})`;
      const insertHtmlStr = `<span class="mark-multiple">M</span> <span class="tooltiptext">${tooltipText}</span>`;
      this.renderer.addClass(el, 'tooltip-container');
      el.innerHTML += insertHtmlStr;
    }

    event.setExtendedProp('loaded', true);
  }

  handleDateSelect(selectInfo: DateSelectArg) {
    const resourceId = selectInfo?.resource?.id;
    const startNumber = moment(selectInfo.startStr).tz(this.timezone).valueOf();
    const endNumber = moment(selectInfo.endStr).tz(this.timezone).valueOf();

    this.checkIsResourceAvailable(startNumber, resourceId).subscribe((res) => {
      if (res) {
        this.addNewParty(startNumber, resourceId, endNumber);
      } else {
        this.confirmationModalService.confirm({
          content: 'Sorry, this resource is not available at this time',
          closeBtnText: 'Cancel',
          confirmBtnText: 'Ok',
        })
          .subscribe();
      }
    });
  }

  handleEventClick(clickInfo: EventClickArg) {
    let { id: partyId, startStr, endStr } = clickInfo.event;
    startStr = moment(startStr).tz(this.timezone).format('hh:mm a');
    endStr = moment(endStr).tz(this.timezone).format('hh:mm a');

    this.editParty({
      partyId,
      startStr,
      endStr,
    });
  }

  handleEventDrop(dropInfo: EventDropArg): void {
    const newResourceId: string = (dropInfo as any).newResource?.id;
    const { id, startStr } = dropInfo.event;
    let selectedParty = this.parties.find(party => party._id === id);
    const start = this.dateTimeService.ISOToMoment(startStr, this.timezone).valueOf();

    selectedParty = {
      ...selectedParty,
      quoted_time: start,
    };

    // It is mean the resource is not the same
    if (newResourceId) {
      const selectedResources: string[] = [newResourceId];
      if (!this.checkResourcesCapacity(this.resources, selectedResources, selectedParty)) {
        dropInfo.revert();

        return;
      }

      const resource: Resource = this.resources.find(resource => resource._id === newResourceId);
      let selectedServices: string[] = this.getIntersection(selectedParty.services, resource.services);
      // Fallback to avoid looping
      if (selectedServices.length === 0) {
        selectedServices = resource.services;
      }
      selectedParty.resources = selectedResources;
      selectedParty.services = selectedServices;
      selectedParty.duration = this.getServicesDuration(this.services, selectedServices);
    }
    this.partyEdit.emit({
      party: selectedParty,
      cardType: FloorPlanPartyType.booked,
      resourceId: newResourceId,
    });
    dropInfo.revert();
  }

  handleEventResize(eventResizeInfo: EventResizeDoneArg): void {
    const { id, startStr, endStr } = eventResizeInfo.event;
    let selectedParty = this.parties.find(party => party._id === id);
    const start = this.dateTimeService.ISOToMoment(startStr, this.timezone).valueOf();
    const end = this.dateTimeService.ISOToMoment(endStr, this.timezone).valueOf();
    const deltaTime = end - start;
    selectedParty = {
      ...selectedParty,
      quoted_time: start,
      duration: deltaTime,
    };
    this.partyEdit.emit({
      party: selectedParty,
      cardType: FloorPlanPartyType.booked,
    });
    eventResizeInfo.revert();
  }

  // Validation methods

  checkIsResourceAvailable(date: number, resourceId: string): Observable<boolean> {
    const selectedResource = this.resources.find(resource => resource._id === resourceId);
    const size = selectedResource.capacity.max;
    const type = PartyTypes.reservation;
    const serviceOrOnlineReservationsEnabled = this.user?.location.serviceSettings?.enabled || this.user?.location.bookingOptions?.onlineReservationsEnabled;
    const overrideNoResources = !serviceOrOnlineReservationsEnabled || !this.user?.location.bookingOptions?.bookingsAllowedPerTimeSlot;
    const time = String(date);
    const { hours, exceptions } = this.generalSchedule;
    const hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      this.reservationDateFilter,
      this.timezone,
    );
    return this.partiesService.findResourcesServicesForNewParty(
      time,
      type,
      [],
      [selectedResource._id],
      size,
      0,
      '',
      overrideNoResources,
      hoursWithDate,
    ).pipe(
      map((data) => {
        if ((data?.overlappingParty || []).length) {
          return this.user?.location.bookingOptions?.allowMultipleParties;
        }

        return Boolean(data?.availableResources?.length);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.updatePartiesCountEvent.complete();
    this.createNewParty.complete();
    this.partyEdit.complete();
    this.partyComplete.complete();
    this.partyDelete.complete();
    this.partyChat.complete();
    this.partyCheckIn.complete();
    this.partyServed.complete();
    this.chooseDate.complete();
  }

  getServicesDuration(allServices: Service[], selectedServicesId: string[]): number {
    return allServices.reduce((duration, service) => {
      return selectedServicesId.includes(service._id) ? duration + service.duration.miliseconds : duration;
    }, 0);
  }

    protected readonly AppIconSize = AppIconSize;
}
